import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CAREER_PREFERENCES = [
  { value: "train-driver", label: "Train driver" },
  { value: "conductor", label: "Conductor" },
  { value: "depot-driver", label: "Depot driver" },
  { value: "maintenance-rolling-stock", label: "Maintenance (Rolling Stock)" },
  { value: "maintenance-track-infrastructure", label: "Maintenance (Track or Infrastructure)" },
  { value: "administrative", label: "Administrative" },
  { value: "graduate", label: "Graduate" },
  { value: "signalling", label: "Signalling" },
  { value: "other", label: "Other" },
];

export default function TalentPoolRegister() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    postcode: "",
    age_years: "",
    target_roles: [],
    preferred_locations: "",
    right_to_work_uk: false,
    english_communication: false,
    basic_literacy_numeracy_digital: false,
    prepared_for_identity_reference_checks: false,
    medical_disclosure_honesty: false,
    understands_shift_patterns: false,
    can_travel_without_public_transport: false,
    willing_to_complete_training: false,
    motivation_for_railway_career: false,
    notes: "",
  });

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleRole = (role) => {
    setForm((prev) => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter((r) => r !== role)
        : [...prev.target_roles, role],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.postcode.trim()) {
      toast.error("Postcode is required");
      return;
    }
    if (!form.target_roles.length) {
      toast.error("Select at least one career preference");
      return;
    }
    setLoading(true);
    try {
      await api.post("/talent-pool/register-interest", {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        postcode: form.postcode || null,
        age_years: parseInt(form.age_years, 10),
        target_roles: form.target_roles,
        preferred_locations: form.preferred_locations || null,
        right_to_work_uk: form.right_to_work_uk,
        english_communication: form.english_communication,
        basic_literacy_numeracy_digital: form.basic_literacy_numeracy_digital,
        prepared_for_identity_reference_checks: form.prepared_for_identity_reference_checks,
        medical_disclosure_honesty: form.medical_disclosure_honesty,
        understands_shift_patterns: form.understands_shift_patterns,
        can_travel_without_public_transport: form.can_travel_without_public_transport,
        willing_to_complete_training: form.willing_to_complete_training,
        motivation_for_railway_career: form.motivation_for_railway_career,
        notes: form.notes || null,
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not register interest");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6 md:p-12">
      <div className="w-full max-w-2xl">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-ink font-semibold" data-testid="tp-register-home">
          ← Railway Careers UK
        </Link>
        <h1 className="font-display font-extrabold text-ink text-3xl md:text-4xl tracking-tight mt-3">
          Talent pool interest
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Register using your email address. After email verification, you must choose a password to finish account setup.
        </p>

        <div className="mt-4 rounded-md border border-amber/40 bg-amber/10 p-4 text-sm text-ink" data-testid="tp-stage-one-intro">
          <div className="font-semibold">Stage One: Railway Talent Pool Eligibility Gateway</div>
          <p className="mt-2 text-ink/80">
            This first assessment checks general eligibility and motivation. It does not assess you for a specific job.
            Applicants who meet the Stage One standard can progress to Stage Two target role selection.
          </p>
        </div>

        {sent ? (
          <div className="mt-8 bg-card border border-border rounded-lg p-8 text-center" data-testid="tp-register-sent">
            <CheckCircle2 className="w-12 h-12 text-lime-600 mx-auto" />
            <h2 className="font-display font-bold text-2xl mt-4 text-ink">Check your inbox</h2>
            <p className="text-sm text-muted-foreground mt-3">
              We sent a verification link to <strong className="text-ink">{form.email}</strong>.
              After you verify your email, you will be asked to create your password.
            </p>
            <div className="mt-6 text-sm text-muted-foreground">
              Already verified?{" "}
              <Link to="/talent-pool/login" className="text-ink font-semibold underline-offset-4 hover:underline">
                Sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 bg-card border border-border rounded-lg p-7 grid md:grid-cols-2 gap-4" data-testid="tp-register-form">
            <div>
              <Label>First name</Label>
              <Input
                required
                className="mt-1"
                value={form.first_name}
                onChange={(e) => update("first_name", e.target.value)}
                data-testid="tp-register-first-name"
              />
            </div>
            <div>
              <Label>Last name</Label>
              <Input
                required
                className="mt-1"
                value={form.last_name}
                onChange={(e) => update("last_name", e.target.value)}
                data-testid="tp-register-last-name"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Email address</Label>
              <Input
                required
                type="email"
                className="mt-1"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="tp-register-email"
              />
            </div>
            <div>
              <Label>Postcode</Label>
              <Input
                required
                className="mt-1 uppercase"
                placeholder="M1 2AB"
                value={form.postcode}
                onChange={(e) => update("postcode", e.target.value)}
                data-testid="tp-register-postcode"
              />
            </div>
            <div>
              <Label>Age</Label>
              <Input
                required
                type="number"
                min={16}
                max={100}
                className="mt-1"
                value={form.age_years}
                onChange={(e) => update("age_years", e.target.value)}
                data-testid="tp-register-age"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Age is used for eligibility checks and is not disclosed to employers.
              </div>
            </div>
            <div className="md:col-span-2 rounded-md border border-border bg-background p-4">
              <div className="font-semibold text-ink">Stage One confirmations</div>
              <div className="mt-3 space-y-2 text-sm">
                {[
                  ["right_to_work_uk", "I have the legal right to work in the UK."],
                  ["english_communication", "I can read, understand and communicate clearly in English."],
                  ["basic_literacy_numeracy_digital", "I have suitable basic literacy, numeracy and digital skills."],
                  ["prepared_for_identity_reference_checks", "I am prepared to undergo identity, reference and employment-history checks."],
                  ["medical_disclosure_honesty", "I am willing to disclose relevant medical conditions and medication honestly."],
                  ["understands_shift_patterns", "I understand some roles involve shifts, weekends, nights and bank holidays."],
                  ["can_travel_without_public_transport", "I can travel to work when public transport may not be available."],
                  ["willing_to_complete_training", "I am willing to complete required training and maintain competence."],
                  ["motivation_for_railway_career", "I am motivated to pursue a railway role or career pathway."],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form[key]}
                      onChange={(e) => update(key, e.target.checked)}
                      data-testid={`tp-register-check-${key}`}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Previous railway experience is not required for most entry-level pathways. A medical condition, disability or previous conviction does not automatically prevent rail employment.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label>Career preferences (select one or more)</Label>
              <div className="mt-2 grid md:grid-cols-2 gap-2 text-sm">
                {CAREER_PREFERENCES.map((opt) => (
                  <label key={opt.value} className="flex items-start gap-2 rounded-md border border-border bg-background px-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.target_roles.includes(opt.value)}
                      onChange={() => toggleRole(opt.value)}
                      data-testid={`tp-register-role-${opt.value}`}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Stage Two: choose the specific railway role or pathway you want to target.
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Preferred locations</Label>
              <Input
                className="mt-1"
                placeholder="Manchester, Liverpool, Leeds"
                value={form.preferred_locations}
                onChange={(e) => update("preferred_locations", e.target.value)}
                data-testid="tp-register-locations"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Notes (optional)</Label>
              <Textarea
                className="mt-1 min-h-[110px]"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                data-testid="tp-register-notes"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="md:col-span-2 bg-navy text-white hover:bg-lime-600 hover:text-slate-950 font-semibold"
              data-testid="tp-register-submit"
            >
              {loading ? "Submitting…" : "Register interest"}
            </Button>
            <div className="md:col-span-2 text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/talent-pool/login" className="text-ink font-semibold underline-offset-4 hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
